// src/components/onboarding/onboarding-stage-content.tsx
import React, { useEffect, useMemo, useState } from "react";
import { isIncomplete } from "@/api/run-completeness";
import { IncompleteRunNotice } from "@/app/components/shared/incomplete-run-notice";
import {
  Download, Check, AlertTriangle, Loader2, Plus, Trash2,
  Users, Lightbulb, MessageSquare, Tag, Calendar, ChevronRight,
  Activity, ExternalLink, FileText, Lock, Unlock,
} from "lucide-react";
import { toast } from "sonner";
import type {
  CustomerDetail,
  Framework,
  StageDefinition,
  ReasoningVariable,
  FrameworkQuery,
  QueryCategory,
  TargetUser,
  IntakePayload,
  CategoryFlag,
  FrameworkGenerationOverview,
  ValidationProblem,
  BatteryBlock,
  BatteryLint,
} from "@/api/onboarding-api";
import {
  diffScoringBasis,
  saveOrApproveFramework,
  unlockFramework,
  downloadFrameworkPdf,
  downloadBaselinePdf,
  downloadBlueprintPdf,
  downloadFrameworkMd,
  downloadBaselineMd,
  downloadBlueprintMd,
  runStage10BrandMonitoring,
  renameBrand,
  proposeCategory,
  getFrameworkGeneration,
  getCustomer,
  lintBattery,
  regenerateBatteryCell,
  USE_CASES,
  VERTICAL_OPTIONS,
} from "@/api/onboarding-api";
import { MODEL_CATALOG } from "@/app/constants/models";
import { Stage4Measurement } from "./stage4-measurement";
// ⚠️ NEW: admin-configurable scoring + in-product human review.
// These REPLACE the old inline WebSocket Stage5Scoring / Stage6Validation
// that used to live in this file (both removed below).
import { Stage5Scoring } from "./stage5-scoring";
import { Stage6HumanReview } from "./stage6-human-review";
import { Stage11RunHistory } from "./stage11-run-history";
import { ConfirmDialog } from "@/app/components/ui/confirm-dialog";
import { Stage2FrameworkGeneration } from "./stage2-framework-generation";
import { BatteryLintPanel, BatteryMatrixView, QuestionContext } from "./stage3-battery";
import { ProposedFactsPanel } from "@/app/components/truth-set/proposed-facts-panel";
import { TruthSetPanel } from "@/app/components/truth-set/truth-set-panel";

/**
 * Models the Stage-10 brand-monitoring scan can OFFER: every catalog entry that
 * is recommended AND available (live) AND runnable. This is the checkbox list,
 * not the initial selection.
 */
const MONITORING_MODEL_CHOICES = MODEL_CATALOG.filter(
  (m) => m.recommended && m.availability === "live" && m.runnable
);

/**
 * The subset PRE-TICKED for the user. Mirrors the backend default in
 * onboarding_service._default_monitoring_models(), which excludes opt-in lanes.
 * Kept separate from the offered list above so an opt-in lane stays selectable
 * while never being chosen on the user's behalf; letting the two collapse back
 * into one list is what auto-enrolled every customer in the first place.
 */
const MONITORING_MODEL_DEFAULTS = MONITORING_MODEL_CHOICES.filter(
  (m) => m.defaultSelected !== false
);

interface Props {
  customer: CustomerDetail;
  stage: StageDefinition;
  isRunning: boolean;
  /** Last error from the global Execute button for this stage, if any.
   *  Used by stages that don't own their own run UI (e.g. Stage 9) to
   *  surface the failure persistently instead of relying on a toast that
   *  disappears within a couple of seconds. */
  stageError?: string | null;
  onCustomerUpdate: (c: CustomerDetail) => void;
  onStageComplete: () => void;
  onIntakeSubmit?: (payload: IntakePayload) => Promise<void>;
  /** Called from the run-history panel (Stage 11) when user clicks "Compare" on a row. */
  onCompareRuns?: (baselineRunId: number, candidateRunId: number) => void;
}

export function OnboardingStageContent({
  customer,
  stage,
  isRunning,
  stageError,
  onCustomerUpdate,
  onStageComplete,
  onIntakeSubmit,
  onCompareRuns,
}: Props) {

  const WhatHappens = (
    <div
      className="rounded-xl p-4 md:p-5"
      style={{
        backgroundColor: "var(--card)",
        border: "1px solid var(--border)",
      }}
    >
      <div className="flex items-center gap-2 mb-3">
        <div
          className="w-7 h-7 rounded-md flex items-center justify-center"
          style={{ backgroundColor: "var(--alpha-teal-10)" }}
        >
          <span style={{ color: "var(--alpha-teal)", fontWeight: 700, fontSize: 12 }}>
            {stage.number}
          </span>
        </div>
        <div>
          <h3 style={{ fontSize: "var(--text-lg)", fontWeight: 600, color: "var(--foreground)", margin: 0 }}>
            {stage.title}
          </h3>
          <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", margin: 0 }}>
            {stage.subtitle}
            {stage.duration_estimate && <> &nbsp;·&nbsp; {stage.duration_estimate}</>}
          </p>
        </div>
      </div>
      <div
        className="text-xs uppercase mb-2 mt-3"
        style={{ color: "var(--muted-foreground)", letterSpacing: "0.05em", fontWeight: 600 }}
      >
        What happens in this stage
      </div>
      <ol className="space-y-2 mt-2">
        {(stage.what_happens || []).map((step, i) => (
          <li key={i} className="flex gap-3" style={{ fontSize: "var(--text-sm)" }}>
            <span
              className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5"
              style={{
                backgroundColor: "var(--muted)",
                color: "var(--muted-foreground)",
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              {i + 1}
            </span>
            <span style={{ color: "var(--foreground)", lineHeight: 1.55 }}>{step}</span>
          </li>
        ))}
      </ol>
    </div>
  );

  let body: React.ReactNode = null;

  switch (stage.number) {
    case 1:
      body = <Stage1Intake customer={customer} onSubmit={onIntakeSubmit} onCustomerUpdate={onCustomerUpdate} disabled={!onIntakeSubmit} />;
      break;
    case 2:
      body = (
        <Stage2FrameworkGeneration
          customer={customer}
          onCustomerUpdate={onCustomerUpdate}
          onStageComplete={onStageComplete}
        />
      );
      break;
    case 3:
      body = (
        <>
          {/* The site read is a Stage 2 input now (#136); its panel lives there. */}
          <ProposedFactsPanel customerId={customer.id} />
          <TruthSetPanel customerId={customer.id} />
          <Stage3FrameworkEditor
            customer={customer}
            onCustomerUpdate={onCustomerUpdate}
            onStageComplete={onStageComplete}
          />
        </>
      );
      break;
    case 4:
      // ⚠️ This now imports the new async Stage4Measurement component
      // from ./stage4-measurement.tsx. The old inline implementation has
      // been removed from this file.
      body = (
        <Stage4Measurement
          customer={customer}
          onCustomerUpdate={onCustomerUpdate}
          onStageComplete={onStageComplete}
          autoStart={isRunning}
        />
      );
      break;
    case 5:
      // ⚠️ NEW admin-configurable scoring: scorer-model picker (with API
      // parameter-pinning note), rubric version, concurrency, per-response
      // evidence, fallback-to-zero. Self-managed — owns its own run button,
      // so renderActionButton() in onboarding-page.tsx returns null for it.
      body = (
        <Stage5Scoring
          customer={customer}
          onCustomerUpdate={onCustomerUpdate}
          onStageComplete={onStageComplete}
        />
      );
      break;
    case 6:
      // ⚠️ NEW in-product human review + Cohen's weighted Kappa (linear).
      // Replaces the old auto-validation stub where kappa was always null.
      // Self-managed (owns its own sample / compute buttons). Accepts
      // onStageComplete so the "Continue to Stage 7" button (shown once
      // Kappa has been computed across all reviewed items) can advance.
      body = (
        <Stage6HumanReview
          customer={customer}
          onCustomerUpdate={onCustomerUpdate}
          onStageComplete={onStageComplete}
        />
      );
      break;
    case 7:
      body = <Stage7GptZero customer={customer} isRunning={isRunning} />;
      break;
    case 8:
      body = <Stage8Baseline customer={customer} isRunning={isRunning} />;
      break;
    case 9:
      body = (
        <Stage9Blueprint
          customer={customer}
          isRunning={isRunning}
          lastError={stageError ?? null}
        />
      );
      break;
    case 10:
      // NEW Stage 10 — mandatory AI brand monitoring (synchronous).
      // Self-managed: it owns its model picker + run button (like the
      // Stage 11 run-history panel), so renderActionButton in
      // onboarding-page.tsx returns null for Stage 10.
      body = (
        <Stage10BrandMonitoring
          customer={customer}
          onCustomerUpdate={onCustomerUpdate}
          onStageComplete={onStageComplete}
        />
      );
      break;
    case 11:
      // Stage 11 run-history view (formerly mis-named stage10-*).
      body = (
        <Stage11RunHistory
          customer={customer}
          onCustomerUpdate={onCustomerUpdate}
          onCompareRuns={(baselineRunId, candidateRunId) => {
            onCompareRuns?.(baselineRunId, candidateRunId);
          }}
        />
      );
      break;
    default:
      body = null;
  }

  return (
    <div className="space-y-5">
      {WhatHappens}
      {body}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// STAGE 1 — Intake form
// ───────────────────────────────────────────────────────────────────────────

// Parse the free-text competitors field into a clean list. A plain split on
// "," would turn an Oxford-comma list ("CareCredit, Cherry, and HFD") into a
// stray "and HFD" entry, so we strip a leading "and"/"&" conjunction from each
// token and de-duplicate. Interior conjunctions are left intact so names like
// "Black & Decker" survive.
function parseCompetitorInput(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(",")) {
    const name = part.trim().replace(/^(?:and|&)\s+/i, "").trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (key === "and" || key === "&" || seen.has(key)) continue;
    seen.add(key);
    out.push(name);
  }
  return out;
}

function Stage1Intake({
  customer,
  onSubmit,
  onCustomerUpdate,
  disabled,
}: {
  customer?: CustomerDetail;
  onSubmit?: (p: any) => Promise<void>;
  onCustomerUpdate?: (c: CustomerDetail) => void;
  disabled: boolean;
}) {
  // Pre-fill from the existing customer's saved intake so that opening Stage 1
  // for an already-onboarded brand shows its data (instead of a blank form).
  // For the create flow, `customer` is the empty dummy record, so these
  // initialise to "".
  const [brand, setBrand] = useState(customer?.brand_name ?? "");
  const [website, setWebsite] = useState(customer?.website_url ?? "");
  const [competitors, setCompetitors] = useState(
    (customer?.competitors ?? []).join(", "),
  );
  const [oneLiner, setOneLiner] = useState(customer?.one_liner ?? "");
  // #136: confirmed at intake. The resolver proposes categories from the name
  // and site; the admin picks or types one.
  const [category, setCategory] = useState(customer?.category ?? "");
  const [vertical, setVerticalChoice] = useState("");
  const [brief, setBrief] = useState(customer?.brief ?? "");
  const [proposals, setProposals] = useState<string[] | null>(null);
  const [sameName, setSameName] = useState<{ name: string; location: string; descriptor: string }[]>([]);
  const [proposing, setProposing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // For an existing customer the brand name is editable here: saving renames
  // the brand, and because brand_name is the canonical key the backend
  // cascades the rename across every table that stores it.
  const isExisting = !!customer && customer.id !== 0;
  const [renaming, setRenaming] = useState(false);
  const [confirmRenameOpen, setConfirmRenameOpen] = useState(false);
  const brandDirty = isExisting && brand.trim().length > 0 && brand.trim() !== customer?.brand_name;

  const propose = async () => {
    if (!brand.trim()) return;
    setProposing(true);
    try {
      const res = await proposeCategory(brand.trim(), website.trim());
      setProposals(res.categories);
      setSameName(res.same_name);
      if (!category.trim() && res.categories.length) setCategory(res.categories[0]);
    } catch (e: any) {
      toast.error(e?.message || "Could not propose a category");
    } finally {
      setProposing(false);
    }
  };

  const intakeReady = !!brand.trim() && !!website.trim() && (isExisting || (!!category.trim() && !!vertical));

  const handle = async () => {
    if (!brand.trim() || !website.trim() || !category.trim() || !vertical) {
      toast.error("Brand name, website URL, category and vertical are required.");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit?.({
        brand_name: brand.trim(),
        website_url: website.trim(),
        competitors: parseCompetitorInput(competitors),
        one_liner: oneLiner.trim(),
        category: category.trim(),
        vertical,
        brief: brief.trim(),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const performRename = async () => {
    if (!customer || !brandDirty) return;
    setRenaming(true);
    try {
      const res = await renameBrand(customer.id, brand.trim());
      toast.success("Brand renamed everywhere.");
      onCustomerUpdate?.(res.customer);
    } catch (e: any) {
      toast.error(e?.message || "Failed to rename brand");
      setBrand(customer.brand_name); // revert on failure
    } finally {
      setRenaming(false);
    }
  };

  return (
    <Card title="Customer intake">
      <div className="space-y-4">
        <Field label="Brand name *">
          <div className="flex gap-2 items-center">
            <input
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              placeholder="e.g. Iterate"
              className="w-full"
              style={inputStyle}
              disabled={(isExisting ? false : disabled) || submitting || renaming}
            />
            {isExisting && (
              <button
                onClick={() => brandDirty && setConfirmRenameOpen(true)}
                disabled={!brandDirty || renaming}
                className="px-3 py-2 rounded-lg flex items-center gap-2 flex-shrink-0"
                style={{
                  backgroundColor: !brandDirty || renaming ? "var(--muted)" : "var(--alpha-teal)",
                  color: !brandDirty || renaming ? "var(--muted-foreground)" : "white",
                  fontSize: "var(--text-sm)", fontWeight: 600, border: "none",
                  cursor: !brandDirty || renaming ? "not-allowed" : "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {renaming && <Loader2 className="w-4 h-4 animate-spin" />}
                Rename
              </button>
            )}
          </div>
          {isExisting && (
            <p style={{ fontSize: 11, color: "var(--muted-foreground)", marginTop: 4 }}>
              brand_name is the key that ties this brand's data together. Renaming
              updates it everywhere — onboarding, monitoring, analytics, publishing
              and brand access.
            </p>
          )}
        </Field>
        <Field label="Website URL *">
          <input
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder="https://iterate.app"
            className="w-full"
            style={inputStyle}
            disabled={disabled || submitting}
          />
        </Field>
        <Field label="Competitors (comma-separated)">
          <input
            value={competitors}
            onChange={(e) => setCompetitors(e.target.value)}
            placeholder="SurveyMonkey, Typeform, Hotjar"
            className="w-full"
            style={inputStyle}
            disabled={disabled || submitting}
          />
        </Field>
        <Field label="One-liner — who is this brand for?">
          <textarea
            value={oneLiner}
            onChange={(e) => setOneLiner(e.target.value)}
            placeholder="Who the brand serves and what it does, in one sentence."
            rows={3}
            className="w-full"
            style={{ ...inputStyle, resize: "vertical" }}
            disabled={disabled || submitting}
          />
        </Field>

        <Field label={isExisting ? "Category (edit at Stage 2)" : "Category *"}>
          <div className="flex gap-2 items-center">
            <input
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="What the brand is, as its buyers would say it"
              className="w-full"
              style={inputStyle}
              disabled={isExisting || disabled || submitting}
            />
            {!isExisting && (
              <button
                onClick={propose}
                disabled={!brand.trim() || proposing || disabled}
                className="px-3 py-2 rounded-lg flex items-center gap-2 flex-shrink-0"
                style={{
                  backgroundColor: "var(--muted)", color: "var(--foreground)", border: "1px solid var(--border)",
                  fontSize: "var(--text-sm)", fontWeight: 500, whiteSpace: "nowrap",
                  cursor: !brand.trim() || proposing ? "not-allowed" : "pointer",
                }}
              >
                {proposing && <Loader2 className="w-4 h-4 animate-spin" />}
                Propose
              </button>
            )}
          </div>
          {proposals && (
            <div className="flex flex-wrap gap-2 mt-2">
              {proposals.length === 0 && (
                <span style={{ fontSize: 11, color: "var(--muted-foreground)" }}>No proposal: type the category.</span>
              )}
              {proposals.map((p) => (
                <button key={p} onClick={() => setCategory(p)}
                  className="px-2 py-1 rounded-md"
                  style={{
                    fontSize: 11, cursor: "pointer",
                    border: `1px solid ${category === p ? "var(--alpha-teal)" : "var(--border)"}`,
                    color: category === p ? "var(--alpha-teal)" : "var(--foreground)", background: "transparent",
                  }}>
                  {p}
                </button>
              ))}
            </div>
          )}
          {sameName.length > 0 && (
            <p style={{ fontSize: 11, color: "var(--muted-foreground)", marginTop: 6 }}>
              Other organisations share this name: {sameName.map((o) => `${o.name}${o.location ? ` (${o.location})` : ""}`).join("; ")}.
              Stage 2 turns them into misidentification gates.
            </p>
          )}
        </Field>
        {!isExisting && (
          <Field label="Vertical *">
            <select value={vertical} onChange={(e) => setVerticalChoice(e.target.value)} className="w-full"
              style={inputStyle} disabled={disabled || submitting}>
              <option value="" disabled>Choose…</option>
              {VERTICAL_OPTIONS.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
            </select>
          </Field>
        )}
        <Field label={isExisting ? "Brief (edit at Stage 2)" : "Brief (optional) — what the brand wants to be recommended for, in the client's words"}>
          <textarea
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            rows={3}
            className="w-full"
            style={{ ...inputStyle, resize: "vertical" }}
            disabled={isExisting || disabled || submitting}
          />
        </Field>

        <div className="pt-2 flex justify-end">
          <button
            onClick={handle}
            disabled={disabled || submitting || !intakeReady}
            className="px-5 py-2.5 rounded-lg flex items-center gap-2 transition-all"
            style={{
              backgroundColor:
                disabled || submitting || !intakeReady
                  ? "var(--muted)"
                  : "var(--alpha-teal)",
              color:
                disabled || submitting || !intakeReady
                  ? "var(--muted-foreground)"
                  : "white",
              fontSize: "var(--text-sm)",
              fontWeight: 600,
              cursor:
                disabled || submitting || !intakeReady
                  ? "not-allowed"
                  : "pointer",
              border: "none",
            }}
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Create customer & continue
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmRenameOpen}
        onOpenChange={setConfirmRenameOpen}
        onConfirm={performRename}
        title={`Rename "${customer?.brand_name ?? ""}" to "${brand.trim()}"?`}
        description={
          <>
            Brand name is the key that ties this brand&apos;s data together, so
            this renames it everywhere — onboarding, monitoring, analytics,
            publishing and brand access. This cannot be undone from the UI.
          </>
        }
        confirmLabel="Rename"
        cancelLabel="Cancel"
        destructive
      />
    </Card>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// STAGE 2 — Framework generation
// ───────────────────────────────────────────────────────────────────────────

// ───────────────────────────────────────────────────────────────────────────
// STAGE 3 — Framework editor + approval
// ───────────────────────────────────────────────────────────────────────────

function Stage3FrameworkEditor({
  customer,
  onCustomerUpdate,
  onStageComplete,
}: {
  customer: CustomerDetail;
  onCustomerUpdate: (c: CustomerDetail) => void;
  onStageComplete: () => void;
}) {
  const [tab, setTab] = useState<"users" | "variables" | "queries" | "variants">("users");
  const [fw, setFw] = useState<Framework>(() =>
    customer.framework ?? { target_users: [], reasoning_variables: [], queries: [], name_variants: [] }
  );
  const [saving, setSaving] = useState(false);
  // Unlock is deliberate and audited, so it is a two-step: reveal a reason
  // field, then confirm. The reason goes to the MANIFEST.
  const [unlocking, setUnlocking] = useState(false);
  const [unlockReason, setUnlockReason] = useState("");
  const [showUnlock, setShowUnlock] = useState(false);
  // Approving an edit that moves the rubric anchors is a two-step, like the
  // unlock: the warning is shown, then confirmed.
  const [anchorsConfirmed, setAnchorsConfirmed] = useState(false);
  // #136: the category-default flags and the validator's view of the saved
  // framework, shown next to each belief and persona.
  const [generation, setGeneration] = useState<FrameworkGenerationOverview | null>(null);

  const locked = !!customer.framework_approved;

  useEffect(() => {
    let cancelled = false;
    getFrameworkGeneration(customer.id)
      .then((g) => { if (!cancelled) setGeneration(g); })
      .catch(() => { if (!cancelled) setGeneration(null); });
    return () => { cancelled = true; };
  }, [customer.id, customer.framework]);
  const problems = generation?.framework_problems ?? [];
  const flags = generation?.category_flags ?? {};

  // #137: the battery linter over the framework as it is being edited. The
  // server lints (the approval refuses on the same result); a short debounce
  // keeps typing from sending a request per keystroke.
  const [lint, setLint] = useState<BatteryLint | null>(null);
  const [regenerating, setRegenerating] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      lintBattery(customer.id, fw)
        .then((result) => { if (!cancelled) setLint(result); })
        .catch(() => { if (!cancelled) setLint(null); });
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [customer.id, fw]);
  const batteryBlocking = !!lint?.gated && !!lint.blocking;

  // Regenerate one persona-by-form cell: save the draft (the worker writes
  // from the saved battery), start the cell, wait for it, reload.
  const regenerateCell = async (personaId: string, form: string) => {
    if (locked || regenerating) return;
    setRegenerating(`${personaId}:${form}`);
    try {
      const saved = await saveOrApproveFramework(customer.id, fw, false);
      onCustomerUpdate(saved.customer);
      const { generation: started } = await regenerateBatteryCell(customer.id, personaId, form);
      let overview: FrameworkGenerationOverview | null = null;
      for (let i = 0; i < 200; i += 1) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        overview = await getFrameworkGeneration(customer.id);
        const g = overview.generation;
        if (g && g.id === started.id && (g.status === "completed" || g.status === "failed")) break;
      }
      setGeneration(overview);
      const g = overview?.generation;
      if (!g || g.id !== started.id || g.status === "running" || g.status === "pending") {
        toast.message("The cell is still being written; reload this screen in a minute.");
      } else if (g.status === "failed") {
        toast.error(g.error || "Regenerating the cell failed; the battery was not changed.");
      } else {
        const fresh = await getCustomer(customer.id);
        onCustomerUpdate(fresh);
        if (fresh.framework) setFw(fresh.framework);
        toast.success("Cell regenerated. The rest of the battery is as it was.");
      }
    } catch (e: any) {
      toast.error(e?.message || "Could not regenerate the cell");
    } finally {
      setRegenerating(null);
    }
  };

  // Rename a variable id and carry every query that referenced it along. Applied
  // on each keystroke rather than on blur so the two halves are never out of
  // step, even mid-edit: each change maps the previous id to the next one, so a
  // rename typed character by character still lands the queries on the final id.
  const renameVariableId = (index: number, nextId: string) => {
    setFw((prev) => {
      const previousId = (prev.reasoning_variables || [])[index]?.id;
      const variables = (prev.reasoning_variables || []).map((v, i) =>
        i === index ? { ...v, id: nextId } : v,
      );
      if (previousId === undefined || previousId === nextId) {
        return { ...prev, reasoning_variables: variables };
      }
      return {
        ...prev,
        reasoning_variables: variables,
        queries: (prev.queries || []).map((q) =>
          (q.variable_ids || []).includes(previousId)
            ? { ...q, variable_ids: (q.variable_ids || []).map((x) => (x === previousId ? nextId : x)) }
            : q,
        ),
      };
    });
  };

  // Queries pointing at variables this framework does not have. Mirrors
  // find_stale_variable_mappings in onboarding_service.py, which is the
  // authority — the server refuses the approval on the same condition. Computing
  // it here is what lets the screen name the queries instead of relaying a
  // rejection after the fact.
  const staleMappings = useMemo(() => {
    const current = new Set((fw.reasoning_variables || []).map((v) => v.id));
    return (fw.queries || [])
      .map((q) => ({
        queryId: q.id,
        staleIds: (q.variable_ids || []).filter((vid) => !current.has(vid)),
      }))
      .filter((entry) => entry.staleIds.length > 0);
  }, [fw.reasoning_variables, fw.queries]);

  // What this edit does to the text the judge scores against. Only worth
  // warning about once the brand has a lineage to invalidate: on a framework
  // that has never been locked there are no stored scores to be judged against
  // anything, so every edit is free.
  const basisDiff = useMemo(
    () => diffScoringBasis(customer.framework, fw),
    [customer.framework, fw],
  );
  // Not while still locked: that save is refused outright, and the lock banner
  // already says so. The warning belongs to the edit the operator can actually
  // commit — after the unlock.
  const warnAboutAnchors =
    !locked && !!customer.framework_ever_locked && basisDiff.affectsStoredScores;

  useEffect(() => {
    setFw(customer.framework ?? { target_users: [], reasoning_variables: [], queries: [], name_variants: [] });
    setShowUnlock(false);
    setUnlockReason("");
    setAnchorsConfirmed(false);
  }, [customer.id, customer.framework_approved]);

  // Editing back to the original anchors retracts the warning, so a confirm
  // given for a change that no longer exists cannot be spent on a later one.
  useEffect(() => {
    if (!warnAboutAnchors) setAnchorsConfirmed(false);
  }, [warnAboutAnchors]);

  const save = async (approve: boolean) => {
    // The server refuses this approval too; catching it here names the queries
    // and opens the tab that fixes them, instead of a toast the operator has to
    // go hunting from. A draft save is deliberately still allowed through — a
    // half-remapped framework has to be parkable.
    if (approve && staleMappings.length > 0) {
      setTab("queries");
      toast.error(
        `${staleMappings.length} quer${staleMappings.length === 1 ? "y" : "ies"} map to variables ` +
        "that no longer exist. Fix them on the Queries tab before approving.",
      );
      return;
    }
    // Warn before the save, not after it: stored scores were judged against the
    // anchors this edit is replacing, and once the rubric hash is re-locked the
    // old text is only recoverable from the chain of custody.
    if (approve && batteryBlocking) {
      setTab("queries");
      toast.error("The question battery does not pass the linter. Fix the red rules and acknowledge the yellow ones on the Queries tab.");
      return;
    }
    if (approve && warnAboutAnchors && !anchorsConfirmed) {
      setAnchorsConfirmed(true);
      return;
    }
    setSaving(true);
    try {
      const res = await saveOrApproveFramework(customer.id, fw, approve);
      onCustomerUpdate(res.customer);
      // The server stamps acknowledgements and re-checks category context on
      // save (#137): edit from what it saved.
      if (res.customer?.framework) setFw(res.customer.framework);
      if (res.battery_check?.error) {
        toast.error(`The category-context check did not run (${res.battery_check.error}); those questions stay unchecked.`);
      }
      getFrameworkGeneration(customer.id).then(setGeneration).catch(() => undefined);
      if (approve) {
        // The brand returns to where it was before the unlock parked it at
        // Stage 3, so say where that is rather than assuming measurement next.
        const stage = res.customer?.current_stage;
        toast.success(
          stage && stage > 4
            ? `Framework approved and re-locked. Stage ${stage} restored — nothing was re-run.`
            : "Framework approved. Ready to run measurement.",
        );
        if (res.library_warning) toast.warning(res.library_warning.message);
        onStageComplete();
      } else {
        toast.success("Draft saved.");
      }
    } catch (e: any) {
      // A 409 means the framework is locked and the edit was refused. Point at
      // the unlock rather than leaving the operator on a dead "Save failed".
      toast.error(e?.message || "Save failed");
      if (locked) setShowUnlock(true);
      getFrameworkGeneration(customer.id).then(setGeneration).catch(() => undefined);
    } finally {
      setSaving(false);
    }
  };

  const unlock = async () => {
    const reason = unlockReason.trim();
    if (!reason) {
      toast.error("Give a reason — it is recorded in the chain of custody.");
      return;
    }
    setUnlocking(true);
    try {
      const res = await unlockFramework(customer.id, reason);
      onCustomerUpdate(res.customer);
      setShowUnlock(false);
      setUnlockReason("");
      toast.success(
        "Framework unlocked. Revise and approve again — the existing Smoke " +
        "calibration no longer applies, so run a fresh one before the baseline.",
      );
    } catch (e: any) {
      toast.error(e?.message || "Unlock failed");
    } finally {
      setUnlocking(false);
    }
  };

  return (
    <Card title="Review & approve framework" subtitle="Edit anything before approval. After approval the framework is locked; unlock to revise it.">
      {locked && (
        <div
          className="flex items-start gap-2 p-3 rounded-lg mb-4"
          style={{
            backgroundColor: "var(--muted)",
            border: "1px solid var(--border)",
            fontSize: "var(--text-xs)",
            color: "var(--muted-foreground)",
          }}
        >
          <Lock className="w-4 h-4 shrink-0" style={{ marginTop: 1 }} />
          <span>
            This framework is <strong>approved and locked</strong> — it is the rubric
            every score in this brand&apos;s lineage is measured against. Edits are
            refused while it is locked. To revise it (for example because Smoke
            calibration flagged a saturated or stuck variable), unlock below: the
            brand returns to Stage 3, the existing calibration stops counting, and
            the unlock is recorded in the chain of custody.
          </span>
        </div>
      )}
      {warnAboutAnchors && (
        <div
          className="flex items-start gap-2 p-3 rounded-lg mb-4"
          style={{
            backgroundColor: "var(--muted)",
            border: "1px solid var(--signal-amber, var(--border))",
            fontSize: "var(--text-xs)",
            color: "var(--foreground)",
          }}
        >
          <AlertTriangle className="w-4 h-4 shrink-0" style={{ marginTop: 1, color: "var(--signal-amber, var(--foreground))" }} />
          <span>
            <strong>This edit changes the scoring rubric, not just its wording.</strong>{" "}
            Every score already stored for this brand was judged against the previous
            anchors, so those scores and the edited rubric no longer describe the same
            measurement. Approving re-locks the framework hash to the new text and
            records the change — it does not re-judge anything.
            {basisDiff.changed.length > 0 && (
              <>
                {" "}Changed:{" "}
                {basisDiff.changed
                  .map((c) => `${c.id} (${c.fields.join(", ")})`)
                  .join("; ")}
                .
              </>
            )}
            {basisDiff.added.length > 0 && <> Added: {basisDiff.added.join(", ")}.</>}
            {basisDiff.removed.length > 0 && (
              <> Removed: {basisDiff.removed.join(", ")} — their stored scores no longer
                map to any variable in the rubric.</>
            )}
            {anchorsConfirmed && (
              <>
                {" "}
                <strong>Click “Approve anyway” again to confirm.</strong>
              </>
            )}
          </span>
        </div>
      )}
      {problems.length > 0 && (
        <div
          className="flex items-start gap-2 p-3 rounded-lg mb-4"
          style={{ border: "1px solid var(--infrared-coral)", color: "var(--infrared-coral)" }}
        >
          <AlertTriangle className="w-4 h-4 flex-shrink-0" style={{ marginTop: 2 }} />
          <span style={{ fontSize: "var(--text-xs)", lineHeight: 1.6 }}>
            <strong>The saved framework does not pass validation ({problems.length}).</strong>{" "}
            Approval is refused until every belief has its three anchors, examples and an evidence
            quote, and every persona its id. Fix them below and save, or regenerate the step at Stage 2.
            <br />
            <span style={{ opacity: 0.85 }}>
              {problems.slice(0, 6).map((p) => p.message).join(" · ")}
              {problems.length > 6 ? ` · and ${problems.length - 6} more` : ""}
            </span>
          </span>
        </div>
      )}
      {batteryBlocking && (
        <div
          className="flex items-start gap-2 p-3 rounded-lg mb-4"
          style={{ border: "1px solid var(--infrared-coral)", color: "var(--infrared-coral)" }}
        >
          <AlertTriangle className="w-4 h-4 flex-shrink-0" style={{ marginTop: 2 }} />
          <span style={{ fontSize: "var(--text-xs)", lineHeight: 1.6 }}>
            <strong>The question battery does not pass the linter.</strong>{" "}
            Approval is blocked while any rule is red or a yellow rule is unacknowledged:{" "}
            {lint!.rules.filter((r) => r.status === "red" || (r.status === "yellow" && !r.acknowledged))
              .map((r) => r.label).join(" · ")}.{" "}
            <button
              onClick={() => setTab("queries")}
              style={{
                color: "inherit", background: "none", border: "none", padding: 0,
                cursor: "pointer", fontSize: "inherit", fontWeight: 700, textDecoration: "underline",
              }}
            >
              Open the Queries tab
            </button>
          </span>
        </div>
      )}
      {staleMappings.length > 0 && (
        <div
          className="flex items-start gap-2 p-3 rounded-lg mb-4"
          style={{
            backgroundColor: "transparent",
            border: "1px solid var(--infrared-coral)",
            color: "var(--infrared-coral)",
          }}
        >
          <AlertTriangle className="w-4 h-4 flex-shrink-0" style={{ marginTop: 2 }} />
          <span style={{ fontSize: "var(--text-xs)", lineHeight: 1.6 }}>
            <strong>
              {staleMappings.length} quer{staleMappings.length === 1 ? "y" : "ies"} map to reasoning
              variables this framework no longer has.
            </strong>{" "}
            The Smoke calibration scores each variable only on the queries mapped to it, so these
            pairings would produce an all-zero calibration table with a Coverage flag on every row
            &mdash; while scoring itself carries on looking healthy. Approval is blocked until they
            are fixed.{" "}
            <button
              onClick={() => setTab("queries")}
              style={{
                color: "inherit", background: "none", border: "none", padding: 0,
                cursor: "pointer", fontSize: "inherit", fontWeight: 700, textDecoration: "underline",
              }}
            >
              Fix on the Queries tab
            </button>
            <br />
            <span style={{ opacity: 0.85 }}>
              {staleMappings
                .slice(0, 10)
                .map((entry) => `${entry.queryId} → ${entry.staleIds.join(", ")}`)
                .join("; ")}
              {staleMappings.length > 10 ? `; and ${staleMappings.length - 10} more` : ""}
            </span>
          </span>
        </div>
      )}
      <div className="flex gap-2 border-b mb-4" style={{ borderColor: "var(--border)" }}>
        {(
          [
            ["users",     "Personas",             Users],
            ["variables", "Beliefs",              Lightbulb],
            ["queries",   "Queries",              MessageSquare],
            ["variants",  "Name variants",        Tag],
          ] as const
        ).map(([key, label, Icon]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className="flex items-center gap-2 px-3 py-2 -mb-px transition-all"
            style={{
              fontSize: "var(--text-sm)",
              fontWeight: tab === key ? 600 : 500,
              color: tab === key ? "var(--alpha-teal)" : "var(--muted-foreground)",
              borderBottom: tab === key ? "2px solid var(--alpha-teal)" : "2px solid transparent",
              background: "transparent",
              cursor: "pointer",
            }}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {tab === "users" && (
        <UsersEditor
          users={fw.target_users}
          onChange={(target_users) => setFw({ ...fw, target_users })}
          problems={problems}
        />
      )}
      {tab === "variables" && (
        <VariablesEditor
          variables={fw.reasoning_variables}
          onChange={(reasoning_variables) => setFw({ ...fw, reasoning_variables })}
          onRenameId={renameVariableId}
          flags={flags}
          problems={problems}
        />
      )}
      {tab === "queries" && (
        <QueriesEditor
          queries={fw.queries}
          variables={fw.reasoning_variables}
          personas={fw.target_users}
          battery={fw.battery ?? {}}
          lint={lint}
          locked={locked}
          onChange={(queries) => setFw({ ...fw, queries })}
          onBatteryChange={(battery) => setFw({ ...fw, battery })}
          onRegenerateCell={regenerateCell}
          regenerating={regenerating}
        />
      )}
      {tab === "variants" && (
        <VariantsEditor variants={fw.name_variants} onChange={(name_variants) => setFw({ ...fw, name_variants })} />
      )}

      <div className="flex items-center justify-between mt-5 pt-4 gap-3 flex-wrap" style={{ borderTop: "1px solid var(--border)" }}>
        {locked ? (
          showUnlock ? (
            <div className="flex items-center gap-2 flex-wrap">
              <input
                value={unlockReason}
                onChange={(e) => setUnlockReason(e.target.value)}
                placeholder="Why is the rubric being revised?"
                className="px-3 py-2 rounded-lg"
                style={{
                  backgroundColor: "var(--background)",
                  color: "var(--foreground)",
                  border: "1px solid var(--border)",
                  fontSize: "var(--text-sm)",
                  minWidth: 260,
                }}
              />
              <button
                onClick={unlock}
                disabled={unlocking}
                className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all"
                style={{
                  background: "transparent",
                  color: "var(--signal-amber, var(--foreground))",
                  fontSize: "var(--text-sm)",
                  fontWeight: 600,
                  border: "1px solid var(--border)",
                  cursor: unlocking ? "not-allowed" : "pointer",
                }}
              >
                <Unlock className="w-4 h-4" />
                {unlocking ? "Unlocking…" : "Confirm unlock"}
              </button>
              <button
                onClick={() => { setShowUnlock(false); setUnlockReason(""); }}
                className="px-3 py-2 rounded-lg transition-all"
                style={{
                  background: "transparent",
                  color: "var(--muted-foreground)",
                  fontSize: "var(--text-sm)",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowUnlock(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all"
              style={{
                backgroundColor: "var(--muted)",
                color: "var(--foreground)",
                fontSize: "var(--text-sm)",
                fontWeight: 500,
                border: "1px solid var(--border)",
                cursor: "pointer",
              }}
            >
              <Unlock className="w-4 h-4" />
              Unlock to revise
            </button>
          )
        ) : (
          <button
            onClick={() => save(false)}
            disabled={saving}
            className="px-4 py-2 rounded-lg transition-all"
            style={{
              backgroundColor: "var(--muted)",
              color: "var(--foreground)",
              fontSize: "var(--text-sm)",
              fontWeight: 500,
              border: "1px solid var(--border)",
              cursor: saving ? "not-allowed" : "pointer",
            }}
          >
            {saving ? "Saving…" : "Save draft"}
          </button>
        )}

        <div className="flex items-center gap-3">
          {customer.framework_approved && (
            <>
              <button
                onClick={() => downloadFrameworkMd(customer).catch((e) => toast.error(e?.message || "Download failed"))}
                className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all"
                style={{
                  background: "transparent",
                  color: "var(--signal-blue)",
                  fontSize: "var(--text-sm)",
                  fontWeight: 500,
                  border: "1px solid var(--signal-blue)",
                  cursor: "pointer",
                }}
              >
                <FileText className="w-4 h-4" />
                Framework MD
              </button>
              <button
                onClick={() => downloadFrameworkPdf(customer).catch((e) => toast.error(e?.message || "Download failed"))}
                className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all"
                style={{
                  background: "transparent",
                  color: "var(--signal-blue)",
                  fontSize: "var(--text-sm)",
                  fontWeight: 500,
                  border: "1px solid var(--signal-blue)",
                  cursor: "pointer",
                }}
              >
                <Download className="w-4 h-4" />
                Framework PDF
              </button>
            </>
          )}
          <button
            onClick={() => save(true)}
            disabled={saving || staleMappings.length > 0 || batteryBlocking}
            title={staleMappings.length > 0
              ? "Some queries map to variables this framework no longer has. Fix them on the Queries tab."
              : batteryBlocking
                ? "The question battery does not pass the linter. See the Queries tab."
                : undefined}
            className="px-5 py-2 rounded-lg flex items-center gap-2 transition-all"
            style={{
              backgroundColor: warnAboutAnchors ? "var(--signal-amber, var(--alpha-teal))" : "var(--alpha-teal)",
              color: "white",
              fontSize: "var(--text-sm)",
              fontWeight: 600,
              border: "none",
              opacity: staleMappings.length > 0 || batteryBlocking ? 0.5 : 1,
              cursor: saving || staleMappings.length > 0 || batteryBlocking ? "not-allowed" : "pointer",
            }}
          >
            {warnAboutAnchors ? <AlertTriangle className="w-4 h-4" /> : <Check className="w-4 h-4" />}
            {warnAboutAnchors
              ? "Approve anyway"
              : customer.framework_approved
                ? "Save & continue"
                : "Approve framework"}
          </button>
        </div>
      </div>
    </Card>
  );
}

/** Problems the validator reported under `prefix` (e.g. "target_users[2]"). */
function problemsAt(problems: ValidationProblem[], prefix: string): ValidationProblem[] {
  return problems.filter((p) => p.path === prefix || p.path.startsWith(`${prefix}.`) || p.path.startsWith(`${prefix}[`));
}

function ProblemList({ problems }: { problems: ValidationProblem[] }) {
  if (problems.length === 0) return null;
  return (
    <ul style={{ fontSize: 11, color: "var(--infrared-coral)", margin: "6px 0 8px 16px", listStyle: "disc" }}>
      {problems.map((p, i) => <li key={i}>{p.message}</li>)}
    </ul>
  );
}

function UsersEditor({
  users, onChange, problems = [],
}: { users: TargetUser[]; onChange: (u: TargetUser[]) => void; problems?: ValidationProblem[] }) {
  const update = (i: number, patch: Partial<TargetUser>) =>
    onChange(users.map((u, idx) => (idx === i ? { ...u, ...patch } : u)));
  const add = () => onChange([...users, { id: `p_${users.length + 1}`, label: "", description: "", goal: "" }]);
  const remove = (i: number) => onChange(users.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-3">
      <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
        Buyer roles who ask AI assistants about this category. Questions name them by id.
      </p>
      {users.map((u, i) => (
        <div key={i} className="p-3 rounded-lg" style={{ backgroundColor: "var(--muted)", border: "1px solid var(--border)" }}>
          <div className="flex items-center gap-2 mb-2">
            <input value={u.id ?? ""} onChange={(e) => update(i, { id: e.target.value })} placeholder="id"
              style={{ ...inputStyle, width: 90 }} />
            <input value={u.label} onChange={(e) => update(i, { label: e.target.value })} placeholder="Role" className="w-full" style={inputStyle} />
            <select value={u.use_case ?? ""} onChange={(e) => update(i, { use_case: (e.target.value || undefined) as TargetUser["use_case"] })}
              style={{ ...inputStyle, width: 160 }}>
              <option value="">Use case…</option>
              {USE_CASES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <textarea value={u.description} onChange={(e) => update(i, { description: e.target.value })} placeholder="Who they are" rows={2} className="w-full mb-2" style={{ ...inputStyle, resize: "vertical" }} />
          <textarea value={u.goal ?? ""} onChange={(e) => update(i, { goal: e.target.value })} placeholder="What they are trying to do" rows={2} className="w-full" style={{ ...inputStyle, resize: "vertical" }} />
          <ProblemList problems={problemsAt(problems, `target_users[${i}]`)} />
          <button onClick={() => remove(i)} className="mt-2 flex items-center gap-1.5 text-xs"
            style={{ color: "var(--infrared-coral)", background: "transparent", border: "none", cursor: "pointer" }}>
            <Trash2 className="w-3 h-3" /> Remove
          </button>
        </div>
      ))}
      <button onClick={add} className="flex items-center gap-2 px-3 py-2 rounded-lg"
        style={{ backgroundColor: "var(--alpha-teal-10)", color: "var(--alpha-teal)", fontSize: "var(--text-sm)", border: "1px dashed var(--alpha-teal-25)", cursor: "pointer", fontWeight: 500 }}>
        <Plus className="w-4 h-4" /> Add persona
      </button>
    </div>
  );
}

function VariablesEditor({
  variables,
  onChange,
  onRenameId,
  flags = {},
  problems = [],
}: {
  variables: ReasoningVariable[];
  onChange: (v: ReasoningVariable[]) => void;
  /** Renaming a variable id is not a local edit: every query's variable_ids
   *  that names the old id has to move with it, or the mapping dangles and the
   *  Smoke calibration goes blind. The parent owns both halves, so the rename
   *  goes up rather than being applied to the variables array here. */
  onRenameId: (index: number, nextId: string) => void;
  /** Category-default check, by belief id (#136). */
  flags?: Record<string, CategoryFlag>;
  problems?: ValidationProblem[];
}) {
  const update = (i: number, patch: Partial<ReasoningVariable>) =>
    onChange(variables.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const setExample = (i: number, level: string, text: string) =>
    update(i, { examples: { ...(variables[i].examples ?? {}), [level]: text } });
  const removeEvidence = (i: number, e: number) =>
    update(i, { evidence: (variables[i].evidence ?? []).filter((_, idx) => idx !== e) });
  const add = () => onChange([...variables, { id: `rv_${variables.length + 1}`, claim: "", rationale: "" }]);
  const remove = (i: number) => onChange(variables.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-3">
      <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
        Concrete mechanism-level beliefs AI models should hold about the brand, each with the evidence that
        supports it and the anchors the judge scores against. Keep them precise.
      </p>
      {variables.map((v, i) => {
        const flag = flags[v.id];
        const here = problemsAt(problems, `reasoning_variables[${i}]`);
        const generated = !!v.evidence || !!v.examples;
        const why = v.why_it_matters ?? v.rationale ?? "";
        return (
        <div key={i} className="p-3 rounded-lg" style={{ backgroundColor: "var(--muted)", border: `1px solid ${here.length || flag?.category_default ? "var(--infrared-coral)" : "var(--border)"}` }}>
          <div className="flex items-center gap-2 mb-2">
            <input value={v.id} onChange={(e) => onRenameId(i, e.target.value)} style={{ ...inputStyle, width: 120 }} />
            <input value={v.label ?? ""} onChange={(e) => update(i, { label: e.target.value })} placeholder="Label" className="w-full" style={inputStyle} />
            <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", whiteSpace: "nowrap" }}>Belief #{i + 1}</span>
          </div>
          {flag?.category_default && (
            <div className="flex items-start gap-2 mb-2" style={{ fontSize: 11, color: "var(--infrared-coral)" }}>
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" style={{ marginTop: 1 }} />
              <span>
                <strong>Category default{flag.current ? "" : " (checked before the beliefs were regenerated)"}:</strong>{" "}
                {flag.reason} Replace it with something only this brand can claim, or keep it knowingly.
              </span>
            </div>
          )}
          <textarea value={v.claim} onChange={(e) => update(i, { claim: e.target.value })} placeholder="The belief in one sentence" rows={2} className="w-full mb-2" style={{ ...inputStyle, resize: "vertical" }} />
          <textarea value={why} onChange={(e) => update(i, v.why_it_matters !== undefined ? { why_it_matters: e.target.value, rationale: e.target.value } : { rationale: e.target.value })} placeholder="Why this matters to the category's buyer" rows={2} className="w-full mb-2" style={{ ...inputStyle, resize: "vertical" }} />
          {generated && (
            <div className="mb-2">
              <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", marginBottom: 4, fontWeight: 600 }}>
                Evidence (quoted word for word from the site or a document)
              </p>
              {(v.evidence ?? []).length === 0 && (
                <p style={{ fontSize: 11, color: "var(--infrared-coral)" }}>No evidence quote: this belief cannot be approved.</p>
              )}
              {(v.evidence ?? []).map((e, ei) => (
                <div key={ei} className="flex items-start gap-2 mb-1" style={{ fontSize: 12 }}>
                  <span style={{ flex: 1, borderLeft: "2px solid var(--alpha-teal)", paddingLeft: 8, color: "var(--foreground)" }}>
                    &ldquo;{e.quote}&rdquo;{" "}
                    {e.url ? (
                      <a href={e.url} target="_blank" rel="noreferrer" style={{ color: "var(--signal-blue)", fontSize: 11 }}>
                        {e.url} <ExternalLink className="w-3 h-3 inline" />
                      </a>
                    ) : (
                      <span style={{ color: "var(--muted-foreground)", fontSize: 11 }}>{e.source_label}</span>
                    )}
                  </span>
                  <button onClick={() => removeEvidence(i, ei)} title="Remove this quote"
                    style={{ color: "var(--infrared-coral)", background: "transparent", border: "none", cursor: "pointer" }}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", marginBottom: 4 }}>
            {generated
              ? "Rubric anchors: what an answer says at each score, and an example answer sentence. The judge scores against these alone."
              : "Scoring rubric (optional) — what each score means to the scorer."}
          </p>
          {(["2", "1", "0"] as const).map((level) => {
            const field = `score_${level}` as "score_2" | "score_1" | "score_0";
            const name = level === "2" ? "stated / explicit" : level === "1" ? "implied / vague" : "absent";
            return (
              <div key={level} className="mb-2">
                <textarea value={v[field] || ""} onChange={(e) => update(i, { [field]: e.target.value } as Partial<ReasoningVariable>)}
                  placeholder={`Score ${level} (${name}): what the answer says`} rows={2} className="w-full"
                  style={{ ...inputStyle, resize: "vertical" }} />
                {generated && (
                  <input value={v.examples?.[level] ?? ""} onChange={(e) => setExample(i, level, e.target.value)}
                    placeholder={`Example answer sentence that scores ${level}`} className="w-full mt-1"
                    style={{ ...inputStyle, fontSize: 12, fontStyle: "italic" }} />
                )}
              </div>
            );
          })}
          <ProblemList problems={here} />
          <button onClick={() => remove(i)} className="mt-2 flex items-center gap-1.5 text-xs"
            style={{ color: "var(--infrared-coral)", background: "transparent", border: "none", cursor: "pointer" }}>
            <Trash2 className="w-3 h-3" /> Remove
          </button>
        </div>
        );
      })}
      <button onClick={add} className="flex items-center gap-2 px-3 py-2 rounded-lg"
        style={{ backgroundColor: "var(--alpha-teal-10)", color: "var(--alpha-teal)", fontSize: "var(--text-sm)", border: "1px dashed var(--alpha-teal-25)", cursor: "pointer", fontWeight: 500 }}>
        <Plus className="w-4 h-4" /> Add belief
      </button>
    </div>
  );
}

/** Short, stable label for a reasoning variable: its claim if it has one, else
 *  its id. Chips show the claim because that is what an operator recognises —
 *  the id is machinery — but the mapping stores the id. */
function variableLabel(v: ReasoningVariable): string {
  const claim = (v.claim || "").trim();
  return claim || v.id;
}

/**
 * The query→variable mapping picker.
 *
 * This mapping used to exist only in the framework JSON: Stage 2 wrote it, the
 * Stage 3 screen never showed it, and renaming a variable (which is the whole
 * point of Stage 3) left every query pointing at an id that no longer existed.
 * Nothing complained — the Smoke calibration just reported zero applicable
 * cells and a Coverage flag for every variable, because a cell is a
 * (response, variable) pair defined through exactly this list. Showing it, and
 * making a stale entry look wrong, is what stops that recurring.
 */
function QueryVariablePicker({
  selected,
  variables,
  onChange,
}: {
  selected: string[];
  variables: ReasoningVariable[];
  onChange: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 px-2 py-0.5 rounded-full"
        style={{
          background: "transparent",
          color: "var(--muted-foreground)",
          border: "1px dashed var(--border)",
          fontSize: "var(--text-xs)",
          cursor: "pointer",
        }}
      >
        <Plus className="w-3 h-3" /> Variable
      </button>
      {open && (
        <>
          {/* Backdrop: closes the panel on any outside click without wiring a
              document listener that would outlive the component. */}
          <div
            onClick={() => setOpen(false)}
            style={{ position: "fixed", inset: 0, zIndex: 40 }}
          />
          <div
            className="rounded-lg p-1"
            style={{
              position: "absolute", zIndex: 41, top: "calc(100% + 4px)", left: 0,
              minWidth: 280, maxWidth: 420, maxHeight: 260, overflowY: "auto",
              backgroundColor: "var(--card)",
              border: "1px solid var(--border)",
              boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            }}
          >
            {variables.length === 0 ? (
              <div className="px-2 py-1.5" style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
                No reasoning variables yet.
              </div>
            ) : (
              variables.map((v) => {
                const on = selected.includes(v.id);
                return (
                  <button
                    key={v.id}
                    onClick={() => toggle(v.id)}
                    className="flex items-start gap-2 w-full text-left px-2 py-1.5 rounded-md"
                    style={{
                      background: on ? "var(--alpha-teal-10)" : "transparent",
                      border: "none", cursor: "pointer",
                      fontSize: "var(--text-xs)",
                      color: "var(--foreground)",
                    }}
                  >
                    <span
                      className="flex items-center justify-center rounded"
                      style={{
                        width: 14, height: 14, marginTop: 1, flexShrink: 0,
                        border: `1px solid ${on ? "var(--alpha-teal)" : "var(--border)"}`,
                        backgroundColor: on ? "var(--alpha-teal)" : "transparent",
                      }}
                    >
                      {on && <Check className="w-2.5 h-2.5" style={{ color: "white" }} />}
                    </span>
                    <span>
                      {variableLabel(v)}
                      <span style={{ color: "var(--muted-foreground)" }}> · {v.id}</span>
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}

function QueriesEditor({
  queries,
  variables,
  personas,
  battery,
  lint,
  locked,
  onChange,
  onBatteryChange,
  onRegenerateCell,
  regenerating,
}: {
  queries: FrameworkQuery[];
  variables: ReasoningVariable[];
  personas: TargetUser[];
  battery: BatteryBlock;
  lint: BatteryLint | null;
  locked: boolean;
  onChange: (q: FrameworkQuery[]) => void;
  onBatteryChange: (b: BatteryBlock) => void;
  onRegenerateCell: (personaId: string, form: string) => void;
  regenerating: string | null;
}) {
  const categories: { key: QueryCategory; label: string; color: string }[] = [
    { key: "direct",      label: "Direct ask",  color: "var(--alpha-teal)" },
    { key: "problem",     label: "Problem",     color: "var(--spectral-violet)" },
    { key: "feature",     label: "Feature",     color: "var(--chart-3)" },
    { key: "comparison",  label: "Comparison",  color: "var(--infrared-coral)" },
    { key: "economics",   label: "Economics",   color: "var(--signal-blue)" },
  ];
  // Any form outside the five (a migrated battery's "integration") still
  // shows, so nothing is hidden from the editor.
  const extra = Array.from(new Set(queries.map((q) => q.category)))
    .filter((c) => !categories.some((cat) => cat.key === c))
    .map((c) => ({ key: c as QueryCategory, label: c || "(no form)", color: "var(--muted-foreground)" }));
  const allCategories = [...categories, ...extra];

  const byId = new Map(variables.map((v) => [v.id, v]));
  const personaOptions = personas.filter((p) => p.id);
  const personaLabel = new Map(personaOptions.map((p) => [p.id as string, p.label]));

  // A filter from the matrix (a cell) or the linter (the questions a finding names).
  const [filter, setFilter] = useState<{ ids?: string[]; cell?: { personaId?: string; beliefId?: string; form: string } } | null>(null);
  const visible = (q: FrameworkQuery) => {
    if (!filter) return true;
    if (filter.ids) return filter.ids.includes(q.id);
    const cell = filter.cell!;
    if (q.category !== cell.form) return false;
    if (cell.personaId) return (q.persona_id || (q.persona_ids || [])[0]) === cell.personaId;
    if (cell.beliefId) return (q.variable_ids || []).includes(cell.beliefId);
    return true;
  };
  // Every lint finding that names a question, shown under it.
  const findingsFor = useMemo(() => {
    const out = new Map<string, { status: string; message: string }[]>();
    for (const rule of lint?.rules ?? []) {
      if (rule.status === "green" || rule.rule === "category_context") continue;
      for (const f of rule.findings) {
        for (const id of f.query_ids) {
          out.set(id, [...(out.get(id) ?? []), { status: rule.status, message: f.message }]);
        }
      }
    }
    return out;
  }, [lint]);

  const update = (i: number, patch: Partial<FrameworkQuery>) =>
    onChange(queries.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  const setPersona = (i: number, personaId: string) =>
    update(i, { persona_id: personaId, persona_ids: personaId ? [personaId] : [], persona: personaLabel.get(personaId) || "" });
  const nextId = () => {
    const used = new Set(queries.map((q) => q.id));
    let n = queries.reduce((top, q) => {
      const m = /^q_(\d+)$/.exec(q.id || "");
      return m ? Math.max(top, Number(m[1])) : top;
    }, 0);
    let id = "";
    do { n += 1; id = `q_${String(n).padStart(2, "0")}`; } while (used.has(id));
    return id;
  };
  const add = (cat: QueryCategory) =>
    onChange([...queries, { id: nextId(), category: cat, text: "", variable_ids: [], persona_id: "", persona_ids: [], use_case: "" }]);
  const remove = (i: number) => onChange(queries.filter((_, idx) => idx !== i));
  const selectStyle: React.CSSProperties = { ...inputStyle, padding: "4px 8px", fontSize: "var(--text-xs)" };

  return (
    <div className="space-y-4">
      <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
        Each question is a scenario one persona meets, in one of five forms, with the use case it stands in and the
        two to four beliefs it is written to surface. The Smoke calibration scores a belief only on the questions
        mapped to it. The linter below runs on every edit; approval waits until no rule is red and every yellow rule
        is acknowledged.
      </p>

      {lint && (
        <>
          {/* A legacy battery is shown, not acted on: marking a cell or
              writing one would put it under the linter at its next approval.
              Regenerating the battery at Stage 2 is the way in. */}
          <BatteryLintPanel lint={lint} battery={battery} onBatteryChange={onBatteryChange}
            locked={locked || !lint.gated} onSelectQuestions={(ids) => setFilter({ ids })} />
          <BatteryMatrixView lint={lint} battery={battery} onBatteryChange={onBatteryChange}
            selectedCell={filter?.cell ?? null}
            onSelectCell={(cell) => setFilter(cell ? { cell } : null)}
            onRegenerateCell={onRegenerateCell} regenerating={regenerating} locked={locked || !lint.gated} />
        </>
      )}

      {filter && (
        <div className="flex items-center gap-2" style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
          Showing {filter.ids ? filter.ids.join(", ") : `${filter.cell!.personaId ?? filter.cell!.beliefId} × ${filter.cell!.form}`}
          <button onClick={() => setFilter(null)}
            style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--signal-blue)", textDecoration: "underline", fontSize: "inherit" }}>
            show all
          </button>
        </div>
      )}

      {allCategories.map((cat) => {
        const items = queries.map((q, idx) => ({ ...q, _idx: idx })).filter((q) => q.category === cat.key && visible(q));
        if (filter && items.length === 0) return null;
        const target = lint?.matrix.targets[cat.key];
        return (
          <div key={cat.key}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                <span style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>{cat.label}</span>
                <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
                  ({queries.filter((q) => q.category === cat.key).length}{target !== undefined ? ` of ${target}` : ""})
                </span>
              </div>
              <button onClick={() => add(cat.key)} className="text-xs flex items-center gap-1"
                style={{ color: cat.color, background: "transparent", border: "none", cursor: "pointer" }}>
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
            <div className="space-y-2">
              {items.map((q) => {
                const selected = q.variable_ids || [];
                const personaId = q.persona_id || (q.persona_ids || [])[0] || "";
                const findings = findingsFor.get(q.id) ?? [];
                const contextVerdict = lint?.context[q.id];
                const red = findings.some((f) => f.status === "red") || contextVerdict?.status === "missing";
                return (
                  <div
                    key={q._idx}
                    className="p-2 rounded-lg"
                    style={{ border: `1px solid ${red ? "var(--infrared-coral)" : "var(--border)"}`, backgroundColor: "var(--muted)" }}
                  >
                    <div className="flex gap-2 items-start">
                      <input value={q.text} onChange={(e) => update(q._idx, { text: e.target.value })} className="flex-1" style={inputStyle} />
                      <button onClick={() => remove(q._idx)} className="p-2 rounded-md"
                        style={{ color: "var(--infrared-coral)", background: "transparent", border: "1px solid var(--border)", cursor: "pointer" }}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <QuestionContext text={q.text} verdict={contextVerdict} />
                    <div className="flex items-center gap-2 flex-wrap mt-2">
                      <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>{q.id}</span>
                      <select value={q.category} onChange={(e) => update(q._idx, { category: e.target.value })} style={selectStyle}
                        title="Form">
                        {allCategories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                      </select>
                      <select value={personaId} onChange={(e) => setPersona(q._idx, e.target.value)} style={selectStyle}
                        title="Persona">
                        <option value="">persona…</option>
                        {personaId && !personaLabel.has(personaId) && <option value={personaId}>{personaId} (not a persona)</option>}
                        {personaOptions.map((p) => <option key={p.id} value={p.id}>{p.label} · {p.id}</option>)}
                      </select>
                      <select value={q.use_case || ""} onChange={(e) => update(q._idx, { use_case: e.target.value })} style={selectStyle}
                        title="Use case">
                        <option value="">use case…</option>
                        {USE_CASES.map((u) => <option key={u.key} value={u.key}>{u.label}</option>)}
                      </select>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap mt-2">
                      {selected.length === 0 && (
                        <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", fontStyle: "italic" }}>
                          no beliefs mapped
                        </span>
                      )}
                      {selected.map((vid) => {
                        const variable = byId.get(vid);
                        // A chip for an id no current variable carries is the
                        // silent failure made visible: this is what zeroes the
                        // calibration table, so it reads as an error, not a tag.
                        const stale = !variable;
                        return (
                          <span
                            key={vid}
                            title={stale
                              ? `"${vid}" is not a variable in this framework. The calibration cannot score it — remap or remove it.`
                              : vid}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
                            style={{
                              fontSize: "var(--text-xs)",
                              maxWidth: 260,
                              color: stale ? "var(--infrared-coral)" : "var(--foreground)",
                              backgroundColor: stale ? "transparent" : "var(--card)",
                              border: `1px solid ${stale ? "var(--infrared-coral)" : "var(--border)"}`,
                              fontWeight: stale ? 600 : 400,
                            }}
                          >
                            {stale && <AlertTriangle className="w-3 h-3" style={{ flexShrink: 0 }} />}
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {stale ? vid : variableLabel(variable!)}
                            </span>
                            <button
                              onClick={() => update(q._idx, { variable_ids: selected.filter((x) => x !== vid) })}
                              aria-label={`Remove ${vid}`}
                              style={{ background: "none", border: "none", cursor: "pointer", padding: 0, lineHeight: 1, color: "inherit" }}
                            >
                              &times;
                            </button>
                          </span>
                        );
                      })}
                      <QueryVariablePicker
                        selected={selected}
                        variables={variables}
                        onChange={(variable_ids) => update(q._idx, { variable_ids })}
                      />
                    </div>
                    {findings.length > 0 && (
                      <ul style={{ margin: "6px 0 0 16px", listStyle: "disc" }}>
                        {findings.map((f, i) => (
                          <li key={i} style={{ fontSize: 11, color: f.status === "red" ? "var(--infrared-coral)" : "var(--foreground)" }}>
                            {f.message}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function VariantsEditor({ variants, onChange }: { variants: string[]; onChange: (v: string[]) => void }) {
  const [draft, setDraft] = useState("");
  return (
    <div className="space-y-3">
      <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
        Brand name variants for matching mentions. Include canonical name + truncations + sub-brands.
      </p>
      <div className="flex flex-wrap gap-2">
        {variants.map((v, i) => (
          <span key={i} className="px-2.5 py-1 rounded-md flex items-center gap-1.5"
            style={{ backgroundColor: "var(--alpha-teal-10)", color: "var(--alpha-teal)", fontSize: "var(--text-xs)", fontWeight: 500, border: "1px solid var(--alpha-teal-25)" }}>
            {v}
            <button onClick={() => onChange(variants.filter((_, idx) => idx !== i))}
              style={{ background: "transparent", border: "none", color: "inherit", cursor: "pointer", padding: 0 }}>×</button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Add variant"
          onKeyDown={(e) => { if (e.key === "Enter" && draft.trim()) { onChange([...variants, draft.trim()]); setDraft(""); } }}
          className="flex-1" style={inputStyle} />
        <button onClick={() => { if (draft.trim()) { onChange([...variants, draft.trim()]); setDraft(""); } }}
          className="px-3 py-2 rounded-lg"
          style={{ backgroundColor: "var(--alpha-teal)", color: "white", fontSize: "var(--text-sm)", fontWeight: 500, border: "none", cursor: "pointer" }}>
          Add
        </button>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// STAGE 5 & 6
// ───────────────────────────────────────────────────────────────────────────
// The old inline Stage5Scoring (WebSocket progress bar) and Stage6Validation
// (kappa always null) used to live here. They have been REMOVED and replaced
// by the self-managed components imported at the top of this file:
//   ./stage5-scoring        → <Stage5Scoring/>        (case 5)
//   ./stage6-human-review   → <Stage6HumanReview/>    (case 6)

// ───────────────────────────────────────────────────────────────────────────
// STAGES 7-9 — Display-only views
// ───────────────────────────────────────────────────────────────────────────

function Stage7GptZero({ customer, isRunning }: { customer: CustomerDetail; isRunning: boolean }) {
  const g = customer.gpt_zero as any;
  const hasG = g && Object.keys(g).length > 0;

  if (isRunning) return <Card title="Running web-retrieval probe"><RunningPlaceholder /></Card>;

  if (!hasG) {
    return (
      <Card title="GPT-zero diagnostic (conditional)">
        <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
          Only runs if any model returned &lt; 2% mention rate. Press <strong>Execute stage</strong> to check.
        </p>
      </Card>
    );
  }

  return (
    <Card title="GPT-zero diagnostic">
      {!g.triggered ? (
        <div className="flex items-center gap-2 py-2" style={{ color: "var(--chart-3)" }}>
          <Check className="w-5 h-5" />
          <span style={{ fontSize: "var(--text-sm)", fontWeight: 500 }}>
            Not triggered — every model passed the 2% mention threshold.
          </span>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-2" style={{ color: "var(--infrared-coral)" }}>
            <AlertTriangle className="w-5 h-5" />
            <span style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>
              Triggered for: {(g.triggered_models || []).join(", ") || "—"}
            </span>
          </div>
          {g.interpretation && (
            <p style={{ fontSize: "var(--text-sm)", color: "var(--foreground)", lineHeight: 1.55 }}>
              {g.interpretation}
            </p>
          )}
        </div>
      )}
    </Card>
  );
}

function Stage8Baseline({ customer, isRunning }: { customer: CustomerDetail; isRunning: boolean }) {
  const b = customer.baseline_report as any;
  const hasB = b && Object.keys(b).length > 0;

  if (isRunning) return <Card title="Building baseline report"><RunningPlaceholder /></Card>;

  if (!hasB) {
    return (
      <Card title="Baseline report (with PDF)">
        <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
          Combines scoring + validation + GPT-zero into the locked baseline. PDF available once generated.
        </p>
      </Card>
    );
  }

  return (
    <Card title="Baseline report">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        <Stat label="Mention rate" value={fmtPct(b.headline?.mention_rate)} />
        <Stat label="Top-5 rate" value={fmtPct(b.headline?.top5_rate)} />
        <Stat label="Primary rec" value={fmtPct(b.headline?.primary_rec_rate)} />
        <Stat label="#1 rate" value={fmtPct(b.headline?.number_one_rate)} />
      </div>
      {isIncomplete(customer.baseline_completeness) && (
        <div className="mb-4">
          <IncompleteRunNotice completeness={customer.baseline_completeness} />
        </div>
      )}
      <div className="flex items-center gap-3">
        <button onClick={() => downloadBaselineMd(customer).catch((e) => toast.error(e?.message || "Download failed"))}
          className="flex items-center gap-2 px-3 py-2 rounded-lg"
          style={{ background: "transparent", color: "var(--signal-blue)", fontSize: "var(--text-sm)", fontWeight: 500, border: "1px solid var(--signal-blue)", cursor: "pointer" }}>
          <FileText className="w-4 h-4" /> Download baseline MD
        </button>
        <button onClick={() => downloadBaselinePdf(customer).catch((e) => toast.error(e?.message || "Download failed"))}
          className="flex items-center gap-2 px-3 py-2 rounded-lg"
          style={{ background: "transparent", color: "var(--signal-blue)", fontSize: "var(--text-sm)", fontWeight: 500, border: "1px solid var(--signal-blue)", cursor: "pointer" }}>
          <Download className="w-4 h-4" /> Download baseline PDF
        </button>
      </div>
    </Card>
  );
}

function Stage9Blueprint({
  customer,
  isRunning,
  lastError,
}: {
  customer: CustomerDetail;
  isRunning: boolean;
  lastError?: string | null;
}) {
  const b = customer.blueprint as any;
  const hasB = b && Object.keys(b).length > 0;

  if (isRunning) return <Card title="Generating intervention blueprint"><RunningPlaceholder /></Card>;

  if (!hasB) {
    return (
      <Card title="Intervention blueprint (PDF + Markdown)">
        <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
          Maps every weak reasoning variable to specific deployment surfaces across Wave 1/2/3.
          Once generated, you can download the blueprint as a Markdown file or a styled PDF.
        </p>
        {lastError && (
          <div
            className="mt-4 p-3 rounded-lg flex items-start gap-2"
            style={{ backgroundColor: "rgba(220,38,38,0.08)", border: "1px solid var(--infrared-coral)" }}
          >
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "var(--infrared-coral)" }} />
            <div style={{ fontSize: "var(--text-xs)", color: "var(--foreground)", lineHeight: 1.55 }}>
              <div style={{ fontWeight: 700, marginBottom: 2, color: "var(--infrared-coral)" }}>
                Last attempt failed
              </div>
              <div style={{ wordBreak: "break-word" }}>{lastError}</div>
              <div style={{ marginTop: 4, color: "var(--muted-foreground)" }}>
                Click <strong>Generate blueprint</strong> below to retry.
              </div>
            </div>
          </div>
        )}
      </Card>
    );
  }

  return (
    <Card title="Intervention blueprint">
      {b.summary && <p style={{ fontSize: "var(--text-sm)", marginBottom: 16, lineHeight: 1.55 }}>{b.summary}</p>}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <Stat label="Wave 1" value={String((b.wave_1 || []).length)} />
        <Stat label="Wave 2" value={String((b.wave_2 || []).length)} />
        <Stat label="Wave 3" value={String((b.wave_3 || []).length)} />
      </div>
      <div className="flex items-center gap-3">
        <button onClick={() => downloadBlueprintMd(customer).catch((e) => toast.error(e?.message || "Download failed"))}
          className="flex items-center gap-2 px-3 py-2 rounded-lg"
          style={{ background: "transparent", color: "var(--signal-blue)", fontSize: "var(--text-sm)", fontWeight: 500, border: "1px solid var(--signal-blue)", cursor: "pointer" }}>
          <FileText className="w-4 h-4" /> Download blueprint MD
        </button>
        <button onClick={() => downloadBlueprintPdf(customer).catch((e) => toast.error(e?.message || "Download failed"))}
          className="flex items-center gap-2 px-3 py-2 rounded-lg"
          style={{ background: "transparent", color: "var(--signal-blue)", fontSize: "var(--text-sm)", fontWeight: 500, border: "1px solid var(--signal-blue)", cursor: "pointer" }}>
          <Download className="w-4 h-4" /> Download blueprint PDF
        </button>
      </div>
    </Card>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// STAGE 10 — AI brand monitoring (mandatory, synchronous, self-managed)
//
// Self-managed (like the Stage 11 run-history panel): it owns its own model
// picker + run button and calls POST /onboarding/customers/{id}/stages/10/run
// directly. The request blocks until the monitoring/antenna pipeline finishes
// and persists into brand_monitoring_runs. renderActionButton in
// onboarding-page.tsx therefore returns null for Stage 10.
// ───────────────────────────────────────────────────────────────────────────

function Stage10BrandMonitoring({
  customer,
  onCustomerUpdate,
  onStageComplete,
}: {
  customer: CustomerDetail;
  onCustomerUpdate: (c: CustomerDetail) => void;
  onStageComplete: () => void;
}) {
  const runId = customer.monitoring_run_id;
  const hasRun = runId != null;

  // The default-on subset starts selected; the rest are offered unticked.
  const [selected, setSelected] = useState<string[]>(
    () => MONITORING_MODEL_DEFAULTS.map((m) => m.id)
  );
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (id: string) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const run = async () => {
    if (selected.length === 0) {
      toast.error("Select at least one model to run monitoring against.");
      return;
    }
    setError(null);
    setRunning(true);
    try {
      const res = await runStage10BrandMonitoring(customer.id, selected);
      onCustomerUpdate(res.customer);
      toast.success("AI brand monitoring complete. Onboarding complete.");
      onStageComplete();
    } catch (e: any) {
      const msg = e?.message || "Brand monitoring failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setRunning(false);
    }
  };

  if (running) {
    const names = MONITORING_MODEL_CHOICES
      .filter((m) => selected.includes(m.id))
      .map((m) => m.label)
      .join(", ");
    return (
      <Card title="Running AI brand monitoring">
        <div className="flex items-center gap-3 py-6">
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--alpha-teal)" }} />
          <div>
            <div style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--foreground)" }}>
              Scanning {customer.brand_name} across {names || "the selected models"}…
            </div>
            <div style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
              This is synchronous and typically takes 2–4 minutes. Keep this tab open.
            </div>
          </div>
        </div>
      </Card>
    );
  }

  if (hasRun) {
    return (
      <Card title="AI brand monitoring complete">
        <div className="flex items-start gap-2 p-3 rounded-lg mb-3"
          style={{ backgroundColor: "var(--alpha-teal-10)", border: "1px solid var(--alpha-teal-25)" }}>
          <Activity className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "var(--alpha-teal)" }} />
          <div style={{ fontSize: "var(--text-sm)", color: "var(--foreground)", lineHeight: 1.55 }}>
            Monitoring run <strong>#{runId}</strong> saved to the Antenna history for{" "}
            <strong>{customer.brand_name}</strong>
            {customer.monitoring_username ? (
              <> (run by <strong>{customer.monitoring_username}</strong>)</>
            ) : null}
            . Open the brand in the Antenna dashboard to view the full report — perception,
            competitive position, attribute signals, business metrics and recommended actions.
            Onboarding for this customer is now complete.
          </div>
        </div>
        <div className="flex items-center gap-2" style={{ color: "var(--muted-foreground)" }}>
          <ExternalLink className="w-3.5 h-3.5" />
          <span style={{ fontSize: "var(--text-xs)" }}>
            Antenna dashboard → search “{customer.brand_name}” → run #{runId}
          </span>
        </div>
      </Card>
    );
  }

  return (
    <Card
      title="Select models & run AI brand monitoring"
      subtitle="Mandatory — onboarding completes only after this finishes. Defaults to all recommended, available models."
    >
      <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)", marginBottom: 14 }}>
        The monitoring/antenna pipeline runs across the selected models, seeded with
        the verified onboarding framework, baseline rates and known competitors. The
        result is saved to the Antenna brand-monitoring history for{" "}
        <strong>{customer.brand_name}</strong>.
      </p>

      <div
        className="text-xs uppercase mb-2"
        style={{ color: "var(--muted-foreground)", letterSpacing: "0.05em", fontWeight: 600 }}
      >
        Models to run against
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
        {MONITORING_MODEL_CHOICES.map((m) => {
          const on = selected.includes(m.id);
          return (
            <button
              key={m.id}
              onClick={() => toggle(m.id)}
              className="flex items-center gap-3 p-3 rounded-lg text-left transition-all"
              style={{
                backgroundColor: on ? "var(--alpha-teal-10)" : "var(--muted)",
                border: `1px solid ${on ? "var(--alpha-teal-25)" : "var(--border)"}`,
                cursor: "pointer",
              }}
            >
              <span
                className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0"
                style={{
                  backgroundColor: on ? "var(--alpha-teal)" : "transparent",
                  border: `1.5px solid ${on ? "var(--alpha-teal)" : "var(--muted-foreground)"}`,
                }}
              >
                {on && <Check className="w-3 h-3" strokeWidth={3} style={{ color: "white" }} />}
              </span>
              <span style={{ fontSize: 16 }}>{m.icon ?? "🔷"}</span>
              <span>
                <span style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--foreground)" }}>
                  {m.modelFamily ?? m.label}
                </span>
                <span style={{ display: "block", fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
                  {m.provider}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {error && (
        <div className="mb-3 p-3 rounded-lg flex items-start gap-2"
          style={{ backgroundColor: "rgba(220,38,38,0.08)", border: "1px solid var(--infrared-coral)" }}>
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "var(--infrared-coral)" }} />
          <span style={{ fontSize: "var(--text-sm)", color: "var(--infrared-coral)" }}>{error}</span>
        </div>
      )}

      <div className="flex items-center justify-between pt-3" style={{ borderTop: "1px solid var(--border)" }}>
        <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
          {selected.length} model{selected.length === 1 ? "" : "s"} selected
        </span>
        <button
          onClick={run}
          disabled={running || selected.length === 0}
          className="px-5 py-2.5 rounded-lg flex items-center gap-2 transition-all"
          style={{
            backgroundColor: selected.length === 0 ? "var(--muted)" : "var(--alpha-teal)",
            color: selected.length === 0 ? "var(--muted-foreground)" : "white",
            fontSize: "var(--text-sm)",
            fontWeight: 600,
            border: "none",
            cursor: running || selected.length === 0 ? "not-allowed" : "pointer",
          }}
        >
          {running && <Loader2 className="w-4 h-4 animate-spin" />}
          <Activity className="w-4 h-4" />
          Run AI brand monitoring
        </button>
      </div>
    </Card>
  );
}

function Stage10Schedule_REMOVED_BUT_KEPT_AS_NOTE() {
  // The old "schedule re-measurements" view became the run-history panel
  // (<Stage11RunHistory>), which is now rendered at Stage 11 (case 11).
  // Stage 10 is now the mandatory AI brand-monitoring step
  // (<Stage10BrandMonitoring>). This stub exists only to mark where the
  // function used to live — it's not called anywhere.
  return null;
}

// ───────────────────────────────────────────────────────────────────────────
// Shared UI helpers
// ───────────────────────────────────────────────────────────────────────────

function Card({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl p-4 md:p-5" style={{ backgroundColor: "var(--card)", border: "1px solid var(--border)" }}>
      <div className="mb-4">
        <h3 style={{ fontSize: "var(--text-base)", fontWeight: 600, color: "var(--foreground)", margin: 0 }}>
          {title}
        </h3>
        {subtitle && (
          <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", margin: "2px 0 0 0" }}>
            {subtitle}
          </p>
        )}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block mb-1.5" style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-3 rounded-lg" style={{ backgroundColor: "var(--muted)", border: "1px solid var(--border)" }}>
      <div style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", marginBottom: 2, fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: "var(--text-lg)", fontWeight: 700, color: "var(--foreground)" }}>{value}</div>
    </div>
  );
}

function RunningPlaceholder() {
  return (
    <div className="flex items-center gap-3 py-4">
      <Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--alpha-teal)" }} />
      <span style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
        Working… results will appear here when complete.
      </span>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: "8px 12px",
  fontSize: "var(--text-sm)",
  color: "var(--foreground)",
  backgroundColor: "var(--background)",
  border: "1px solid var(--border)",
  borderRadius: 8,
  outline: "none",
  fontFamily: "inherit",
};

function fmtPct(v: any): string {
  if (v == null || isNaN(Number(v))) return "—";
  return `${(Number(v) * 100).toFixed(1)}%`;
}